import React, { useMemo, useState, useEffect } from 'react';
import {
  EntityAutocompletePicker,
  EntityLifecycleFilter,
  EntityNamespaceFilter,
  EntityFilter,
  useEntityList,
  CatalogAutocomplete,
} from '@backstage/plugin-catalog-react';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import { Entity, parseEntityRef } from '@backstage/catalog-model';
import { Box, Checkbox, makeStyles } from '@material-ui/core';

const useStyles = makeStyles({
  root: {
    paddingBottom: 8,
    paddingTop: 8,
  },
});

/**
 * Custom Lifecycle Picker that displays "Ring" instead of "Lifecycle"
 * Uses the same EntityAutocompletePicker as the built-in lifecycle picker
 * for consistent styling
 */
export const CustomLifecyclePicker = (props: { initialFilter?: string[] }) => {
  const { initialFilter = [] } = props;
  
  return (
    <EntityAutocompletePicker
      label="Ring"
      name="lifecycles"
      path="spec.lifecycle"
      Filter={EntityLifecycleFilter}
      initialSelectedOptions={initialFilter}
    />
  );
};

/**
 * Custom filter class for filtering entities by system (spec.system)
 */
class EntitySystemFilter implements EntityFilter {
  constructor(readonly values: string[]) {}

  filterEntity(entity: Entity): boolean {
    const system = (entity.spec as { system?: string })?.system;
    if (!system) return false;
    
    // Extract just the name from the system reference for comparison
    const systemName = system.includes('/') ? system.split('/').pop() : system;
    return this.values.some(v => {
      const valueName = v.includes('/') ? v.split('/').pop() : v;
      return systemName === valueName || system === v;
    });
  }

  toQueryValue(): string[] {
    return this.values;
  }
}

/**
 * Helper to extract just the name from a full entity reference
 * e.g., "system:default/kflow-lab-ml-westus-25" -> "kflow-lab-ml-westus-25"
 */
const getDisplayName = (fullRef: string): string => {
  try {
    const parsed = parseEntityRef(fullRef);
    return parsed.name;
  } catch {
    // If parsing fails, try simple extraction
    if (fullRef.includes('/')) {
      return fullRef.split('/').pop() || fullRef;
    }
    return fullRef;
  }
};

/**
 * Custom System Picker that displays "Stamp" instead of "System"
 * Shows only the system name (without "system:default/" prefix)
 */
export const CustomSystemPicker = (props: { initialFilter?: string[] }) => {
  const { initialFilter = [] } = props;
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const { updateFilters, filters, queryParameters } = useEntityList();
  
  const systemsParameter = queryParameters.systems;
  
  // Fetch available systems from the catalog
  const [availableSystems, setAvailableSystems] = useState<string[]>([]);
  
  useEffect(() => {
    const fetchSystems = async () => {
      try {
        const { facets } = await catalogApi.getEntityFacets({
          facets: ['spec.system'],
        });
        const systems = facets['spec.system']?.map(f => f.value) || [];
        setAvailableSystems(systems);
      } catch (e) {
        console.error('Failed to fetch systems', e);
      }
    };
    fetchSystems();
  }, [catalogApi]);

  // Get currently selected systems
  const selectedSystems = useMemo(() => {
    if (Array.isArray(systemsParameter)) {
      return systemsParameter as string[];
    }
    if (systemsParameter) {
      return [systemsParameter as string];
    }
    return (filters.systems as EntitySystemFilter)?.values ?? initialFilter;
  }, [systemsParameter, filters.systems, initialFilter]);

  // Create display options (just the name, not the full reference)
  const displayOptions = useMemo(() => {
    return availableSystems.map(getDisplayName).sort();
  }, [availableSystems]);

  // Map from display name to full reference
  const displayToFullRef = useMemo(() => {
    const map: Record<string, string> = {};
    availableSystems.forEach(fullRef => {
      map[getDisplayName(fullRef)] = fullRef;
    });
    return map;
  }, [availableSystems]);

  // Convert selected full refs to display names
  const selectedDisplayNames = useMemo(() => {
    return selectedSystems.map(getDisplayName);
  }, [selectedSystems]);

  const handleChange = (_event: React.ChangeEvent<{}>, displayNames: string[]) => {
    // Convert display names back to full references
    const fullRefs = displayNames.map(name => displayToFullRef[name] || name);
    updateFilters({
      systems: fullRefs.length > 0 ? new EntitySystemFilter(fullRefs) : undefined,
    });
  };

  if (displayOptions.length === 0) {
    return null;
  }

  return (
    <Box className={classes.root}>
      <CatalogAutocomplete
        multiple
        disableCloseOnSelect
        label="Stamp"
        name="systems-picker"
        options={displayOptions}
        value={selectedDisplayNames}
        onChange={handleChange}
        renderOption={(option, { selected }) => (
          <>
            <Checkbox
              checked={selected}
              size="small"
              style={{ marginRight: 8 }}
            />
            {option}
          </>
        )}
      />
    </Box>
  );
};

/**
 * Custom Namespace Picker that displays "Tenant" instead of "Namespace"
 * Uses the same EntityAutocompletePicker for consistent styling
 */
export const CustomNamespacePicker = (props: { initialFilter?: string[] }) => {
  const { initialFilter = [] } = props;
  
  return (
    <EntityAutocompletePicker
      label="Tenant"
      name="namespaces"
      path="metadata.namespace"
      Filter={EntityNamespaceFilter}
      initialSelectedOptions={initialFilter}
    />
  );
};
